import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { AlignJustify, Eye, EyeOff, AlertTriangle, FileCode, GitBranch, ChevronUp, ChevronDown, ArrowUp } from 'lucide-react';
import EDIPrettyViewer from './EDIPrettyViewer';
import JsonSchemaTreeViewer from './JsonSchemaTreeViewer';

const DiffViewer = ({
    source,
    target,
    sourceName = 'Source',
    targetName = 'Target',
    highlightedField = null,
    suggestedRenames = [],
    diffActions = [],
    typeChanges = [],
    cascadingImpacts = [],
    formattingOnly = [],
    diffResult = null,
    isFullscreen = false,
    settings = {},
    isViewMode = false,
    searchQuery = '',
    searchType = 'all',
    currentMatchIndex = 0,
    changesFilter = 'all',
    showSchemaDriftOnly = false,
    onSearchMatchesChange,
    showTypes = true,
    onEdiStats,
    elementChanges = []
}) => {
    const [viewMode, setViewMode] = useState('tree');
    const [showFormattingChanges, setShowFormattingChanges] = useState(true);
    const [currentNavIdx, setCurrentNavIdx] = useState(0);
    const contentRef = useRef(null);
    const lastHighlightedRef = useRef(null);
    const toggleFormattingChanges = useCallback(() => setShowFormattingChanges(prev => !prev), []);

    const isEdiFile = useMemo(() => {
        const sourceFormat = source?.detected_format || '';
        const targetFormat = target?.detected_format || '';
        return sourceFormat.startsWith('x12') || targetFormat.startsWith('x12');
    }, [source, target]);

    const isJsonFile = useMemo(() => {
        const sourceFormat = source?.detected_format || '';
        const targetFormat = target?.detected_format || '';
        const hasData = source || target;
        return hasData && !sourceFormat.startsWith('x12') && !targetFormat.startsWith('x12');
    }, [source, target]);

    // Auto-switch view based on format
    useEffect(() => {
        if (isEdiFile) {
            setViewMode('edi');
        } else if (isJsonFile) {
            setViewMode('tree');
        } else {
            setViewMode('raw');
        }
    }, [isEdiFile, isJsonFile]);

    // Reset nav index when view or data changes
    useEffect(() => { setCurrentNavIdx(0); }, [viewMode, diffResult]);

    // --- Navigation: get all changed DOM nodes in the tree ---
    const getChangedNodes = useCallback(() => {
        if (!contentRef.current) return [];
        return Array.from(contentRef.current.querySelectorAll('[data-change="true"]'));
    }, []);

    const goToChange = useCallback((idx) => {
        const nodes = getChangedNodes();
        if (!nodes.length) return;
        const newIdx = Math.max(0, Math.min(nodes.length - 1, idx));
        setCurrentNavIdx(newIdx);
        const el = nodes[newIdx];
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (lastHighlightedRef.current) {
            lastHighlightedRef.current.style.outline = '';
            lastHighlightedRef.current.style.outlineOffset = '';
        }
        if (el) {
            el.style.outline = '1px solid rgb(96 165 250)';
            el.style.outlineOffset = '-1px';
            lastHighlightedRef.current = el;
            setTimeout(() => { el.style.outline = ''; el.style.outlineOffset = ''; }, 2000);
        }
    }, [getChangedNodes]);

    const goToTop = useCallback(() => {
        setCurrentNavIdx(0);
        // Scroll the nearest scrollable ancestor
        const el = contentRef.current;
        if (el) el.scrollTop = 0;
        // Also try the results container above
        const allScrollable = document.querySelectorAll('[class*="overflow-y-auto"]');
        for (const s of allScrollable) {
            if (s.scrollTop > 0) { s.scrollTop = 0; break; }
        }
    }, []);

    // Eye button: scroll to matching field in tree
    useEffect(() => {
        if (!highlightedField || viewMode !== 'tree') return;
        setTimeout(() => {
            const el = contentRef.current?.querySelector(`[data-nodeid$=".${highlightedField}"], [data-nodeid="${highlightedField}"]`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.style.outline = '1px solid rgb(96 165 250)';
                el.style.outlineOffset = '-1px';
                setTimeout(() => { el.style.outline = ''; el.style.outlineOffset = ''; }, 2000);
            }
        }, 100);
    }, [highlightedField, viewMode]);

    // Count changed nodes for the nav widget
    const [changedCount, setChangedCount] = useState(0);
    useEffect(() => {
        const timer = setTimeout(() => {
            setChangedCount(getChangedNodes().length);
        }, 300);
        return () => clearTimeout(timer);
    }, [diffResult, viewMode, getChangedNodes]);

    // j/k and arrow key navigation
    useEffect(() => {
        const handler = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
            if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); goToChange(currentNavIdx + 1); }
            else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); goToChange(currentNavIdx - 1); }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [goToChange, currentNavIdx]);

    // --- Raw view helpers ---
    const syntaxHighlight = (line) => {
        if (!line || line.trim() === '') return <span className="text-gray-600">&nbsp;</span>;
        const highlighted = line
            .replace(/"([^"]+)":/g, '<span class="text-[#9cdcfe]">"$1"</span>:')
            .replace(/: "([^"]+)"/g, ': <span class="text-green-400">"$1"</span>')
            .replace(/: (\d+(?:\.\d+)?)/g, ': <span class="text-orange-400">$1</span>')
            .replace(/: (true|false)/g, ': <span class="text-purple-400">$1</span>')
            .replace(/: (null)/g, ': <span class="text-gray-500">$1</span>');
        return <span className="font-mono text-xs whitespace-pre" dangerouslySetInnerHTML={{ __html: highlighted }} />;
    };

    const getRawLines = useCallback((data) => {
        if (!data) return [];
        if (isEdiFile && data.segments) {
            return data.segments.map(s => s.raw || `${s.segment_id}*${(s.elements || []).join('*')}`);
        }
        try {
            const obj = typeof data === 'string' ? JSON.parse(data) : data;
            const filtered = Object.fromEntries(Object.entries(obj).filter(([k]) => k !== 'detected_format'));
            return JSON.stringify(filtered, null, 2).split('\n');
        } catch {
            return [String(data)];
        }
    }, [isEdiFile]);

    const sourceLines = useMemo(() => getRawLines(source), [source, getRawLines]);
    const targetLines = useMemo(() => getRawLines(target), [target, getRawLines]);

    const handleRawCopy = (e) => {
        const selection = window.getSelection();
        if (selection && selection.toString()) {
            e.clipboardData.setData('text/plain', selection.toString());
            e.preventDefault();
        }
    };

    const renderRawPane = (lines, label) => (
        <div className="flex-1 overflow-auto min-w-0" onCopy={handleRawCopy}>
            <div className="sticky top-0 z-10 bg-[#2d2d2d] px-3 py-1 border-b border-[#3c3c3c]">
                <span className="font-bold text-xs text-gray-300">{label}</span>
            </div>
            {lines.map((line, idx) => (
                <div key={idx} className="flex items-stretch hover:bg-white/[0.03]">
                    <span className="w-10 text-right text-gray-400 text-xs select-none pr-2 bg-gray-800 border-r border-[#3c3c3c] flex-shrink-0 flex items-center justify-end">
                        {idx + 1}
                    </span>
                    <div className="flex items-center flex-1 py-0.5 px-2 font-mono text-xs">
                        {syntaxHighlight(line)}
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className={`diff-viewer flex flex-col bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg overflow-hidden ${isFullscreen ? 'h-full' : ''}`}>
            {/* Toolbar */}
            <div className="flex items-center justify-between px-2 py-1.5 bg-[#252526] border-b border-[#3c3c3c]">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => isJsonFile && setViewMode('tree')}
                            disabled={!isJsonFile}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'tree' ? 'bg-blue-900/30 text-blue-400' : isJsonFile ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600'}`}
                            title={isJsonFile ? 'Schema Tree View' : 'No structured data detected'}
                        >
                            <GitBranch size={12} />
                            Tree
                        </button>
                        <button
                            onClick={() => setViewMode('raw')}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'raw' ? 'bg-blue-900/30 text-blue-400' : 'text-gray-400 hover:text-gray-300'}`}
                        >
                            <AlignJustify size={12} />
                            Raw
                        </button>
                        {!isViewMode && (
                            <button
                                onClick={() => isEdiFile && setViewMode('edi')}
                                disabled={!isEdiFile}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'edi' ? 'bg-blue-900/30 text-blue-400' : isEdiFile ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600'}`}
                                title={isEdiFile ? 'EDI Pretty View' : 'No EDI file detected'}
                            >
                                <FileCode size={12} />
                                EDI
                            </button>
                        )}
                    </div>

                    {typeChanges.length > 0 && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-900/30 text-yellow-400 rounded text-xs font-medium">
                            <AlertTriangle size={12} />
                            {typeChanges.length} type
                        </span>
                    )}

                    {formattingOnly.length > 0 && (
                        <button
                            onClick={toggleFormattingChanges}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${showFormattingChanges ? 'bg-[#3c3c3c] text-gray-400' : 'bg-[#1e1e1e] text-gray-500'}`}
                            title={showFormattingChanges ? 'Hide formatting-only changes' : 'Show formatting-only changes'}
                        >
                            {showFormattingChanges ? <Eye size={12} /> : <EyeOff size={12} />}
                            {formattingOnly.length} fmt
                        </button>
                    )}
                </div>

            </div>

            {/* Content */}
            <div ref={contentRef} className="flex-1 overflow-auto">
                {viewMode === 'tree' ? (
                    <JsonSchemaTreeViewer
                        source={source}
                        target={isViewMode ? null : target}
                        diffResult={diffResult}
                        suggestedRenames={suggestedRenames}
                        typeChanges={typeChanges}
                        sourceName={sourceName}
                        targetName={targetName}
                        searchQuery={searchQuery}
                        searchType={searchType}
                        changesFilter={changesFilter}
                        currentMatchIndex={currentMatchIndex}
                        onSearchMatchesChange={onSearchMatchesChange}
                        showTypes={showTypes}
                    />
                ) : viewMode === 'edi' ? (
                    <EDIPrettyViewer
                        source={source}
                        target={isViewMode ? null : target}
                        sourceName={isViewMode ? 'File' : sourceName}
                        targetName={targetName}
                        isViewMode={isViewMode}
                        searchQuery={searchQuery}
                        searchType={searchType}
                        currentMatchIndex={currentMatchIndex}
                        changesFilter={changesFilter}
                        showSchemaDriftOnly={showSchemaDriftOnly}
                        onSearchMatchesChange={onSearchMatchesChange}
                        viewerSettings={settings}
                        highlightedSegmentId={highlightedField}
                        onEdiStats={onEdiStats}
                        suggestedRenames={suggestedRenames}
                        elementChanges={elementChanges}
                        typeChanges={typeChanges}
                    />
                ) : viewMode === 'raw' ? (
                    <div className="flex h-full">
                        {renderRawPane(sourceLines, sourceName)}
                        {!isViewMode && target && (
                            <>
                                <div className="w-px bg-[#3c3c3c] flex-shrink-0" />
                                {renderRawPane(targetLines, targetName)}
                            </>
                        )}
                    </div>
                ) : null}
            </div>

            {/* Floating Navigation Widget */}
            {viewMode !== 'edi' && changedCount > 0 && (
                <div className="fixed bottom-0 right-0 z-50 flex items-center gap-2 bg-gray-800/95 p-2 rounded-tl-2xl border-t border-l border-[#3c3c3c] backdrop-blur-sm">
                    <button
                        onClick={() => goToChange(currentNavIdx - 1)}
                        disabled={currentNavIdx === 0}
                        className="p-1 text-gray-300 hover:text-white disabled:opacity-30 rounded-full hover:bg-gray-700 transition-colors"
                        title="Previous change"
                    >
                        <ChevronUp size={18} />
                    </button>
                    <div className="flex items-center gap-1 px-2.5 font-bold text-xs text-gray-300 min-w-[65px] justify-center border-l border-r border-[#3c3c3c] mx-0.5">
                        <span className="text-white">{currentNavIdx + 1}</span>
                        <span className="text-gray-500">/</span>
                        <span>{changedCount}</span>
                    </div>
                    <button
                        onClick={() => goToChange(currentNavIdx + 1)}
                        disabled={currentNavIdx >= changedCount - 1}
                        className="p-1 text-gray-300 hover:text-white disabled:opacity-30 rounded-full hover:bg-gray-700 transition-colors"
                        title="Next change"
                    >
                        <ChevronDown size={18} />
                    </button>
                    <button
                        onClick={goToTop}
                        className="p-1 ml-0.5 text-blue-400 hover:text-blue-300 rounded-full hover:bg-blue-900/20 transition-colors"
                        title="Back to Top"
                    >
                        <ArrowUp size={18} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default DiffViewer;
